import { Analytics } from '@cobliteam/analytics';
import type { Insight } from '@cobliteam/api-lib/src/__generated__/graphql';
import { useInsights } from '@cobliteam/hooks';
import { Dialog, Typography } from '@cobliteam/ui/components';
import { cn } from '@cobliteam/ui/utils';
import { addDays, startOfWeek, subDays, subWeeks } from '@cobliteam/utils/date';
import { useEffect, useState } from 'react';
import insightsUtils from '../../utils';
import LandingContent from '../LandingContent';
import WeekInsights from '../WeekInsights/WeekInsights';
import InsightContent from './InsightContent';

interface InsightsProps {
  open: boolean;
  insight?: Insight;
  onOpen: (open: boolean) => void;
}

const TOTAL_WEEKS_TO_SHOW = 4;

const InsightsModal = ({ open, insight, onOpen }: InsightsProps) => {
  const { useGetInsights } = useInsights;
  const { markInsightAsRead, filterOldInsights, getReadInsights } = insightsUtils();

  const [readInsights, setReadInsights] = useState<{ [id: string]: string }>(getReadInsights());
  const [selectedInsight, setSelectedInsight] = useState<Insight | undefined>(undefined);
  const [isFirstInsight, setIsFirstInsight] = useState(false);
  const [isLastInsight, setIsLastInsight] = useState(false);

  useEffect(() => {
    setSelectedInsight(insight);
    if (insight) {
      const updatedReadInsights = markInsightAsRead(insight);
      setReadInsights(updatedReadInsights || getReadInsights());
    }
  }, [insight]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: filterOldInsights is stable and should run once on mount
  useEffect(() => {
    filterOldInsights(getReadInsights());
  }, []);

  const getWeekRequestPeriods = () => {
    const currentStartWeek = startOfWeek(new Date());

    return Array.from({ length: TOTAL_WEEKS_TO_SHOW }, (_, i) => i).map((i) => {
      const weekSundayStart = subDays(subWeeks(currentStartWeek, i), 1);
      const weekMondayEnd = addDays(weekSundayStart, 2);

      return { from: weekSundayStart, to: weekMondayEnd };
    });
  };

  const handleInsightClick = (insight: Insight) => {
    const updatedReadInsights = markInsightAsRead(insight);
    setReadInsights(updatedReadInsights);
    setSelectedInsight(insight);
    const lastWeek = allInsights[allInsights.length - 1];
    const lastInsight = lastWeek[lastWeek.length - 1];
    if (lastInsight && insight?.id === lastInsight.id) {
      setIsFirstInsight(false);
      setIsLastInsight(true);
      return;
    }

    const firstWeek = allInsights[0];
    const firstInsight = firstWeek[0];
    if (firstInsight && insight?.id === firstInsight.id) {
      setIsFirstInsight(true);
      setIsLastInsight(false);
      return;
    }

    setIsFirstInsight(false);
    setIsLastInsight(false);
  };

  const handleNextInsightClick = () => {
    if (!selectedInsight) return;

    Analytics.track('Insights Modal Next Button Clicked');

    setIsFirstInsight(false);

    const currentInsightWeekIndex = allInsights.findIndex((insights) => insights.includes(selectedInsight));
    const weekInsights = allInsights[currentInsightWeekIndex];
    const currentInsightIndex = weekInsights.findIndex((insight) => insight.id === selectedInsight.id);

    const isLastWeek = currentInsightWeekIndex === allInsights.length - 1;
    const isLastWeekInsight = currentInsightIndex === weekInsights.length - 1;

    // last insight from the week and has no more weeks
    if (isLastWeekInsight && isLastWeek) return;

    // last insight from week, but still have more
    if (isLastWeekInsight) {
      const nextWeekInsights = allInsights[currentInsightWeekIndex + 1];
      setSelectedInsight(nextWeekInsights[0]);
      const updatedReadInsights = markInsightAsRead(nextWeekInsights[0]);
      setReadInsights(updatedReadInsights);
      return;
    }

    // has more insights on current week
    const nextInsight = weekInsights[currentInsightIndex + 1];
    setSelectedInsight(nextInsight);
    const updatedReadInsights = markInsightAsRead(nextInsight);
    setReadInsights(updatedReadInsights);

    // is last week and next is last insight
    if (isLastWeek && currentInsightIndex === weekInsights.length - 2) {
      setIsLastInsight(true);
    }
  };

  const handlePreviousInsightClick = () => {
    if (!selectedInsight) return;

    Analytics.track('Insights Modal Previous Button Clicked');

    setIsLastInsight(false);

    const currentInsightWeekIndex = allInsights.findIndex((insights) => insights.includes(selectedInsight));
    const weekInsights = allInsights[currentInsightWeekIndex];
    const currentInsightIndex = weekInsights.findIndex((insight) => insight.id === selectedInsight.id);

    const isFirstWeek = currentInsightWeekIndex === 0;
    const isFirstWeekInsight = currentInsightIndex === 0;

    // first insight from the week and has no more previous weeks
    if (isFirstWeekInsight && isFirstWeek) return;

    // first insight from week, but still have more
    if (isFirstWeekInsight) {
      const previousWeekInsights = allInsights[currentInsightWeekIndex - 1];
      setSelectedInsight(previousWeekInsights[previousWeekInsights.length - 1]);
      const updatedReadInsights = markInsightAsRead(previousWeekInsights[previousWeekInsights.length - 1]);
      setReadInsights(updatedReadInsights);
      return;
    }

    // has more insights on current week
    const previousInsight = weekInsights[currentInsightIndex - 1];
    setSelectedInsight(previousInsight);
    const updatedReadInsights = markInsightAsRead(previousInsight);
    setReadInsights(updatedReadInsights);

    // is first week and previous is first insight
    if (isFirstWeek && currentInsightIndex === 1) {
      setIsFirstInsight(true);
    }
  };

  const weekPeriods = getWeekRequestPeriods();
  const weekPeriodsIsoStrings = weekPeriods.map((week) => ({ from: week.from.toISOString(), to: week.to.toISOString() }));

  const insightsWeek0 = useGetInsights({ from: weekPeriodsIsoStrings[0].from, to: weekPeriodsIsoStrings[0].to });
  const insightsWeek1 = useGetInsights({ from: weekPeriodsIsoStrings[1].from, to: weekPeriodsIsoStrings[1].to });
  const insightsWeek2 = useGetInsights({ from: weekPeriodsIsoStrings[2].from, to: weekPeriodsIsoStrings[2].to });
  const insightsWeek3 = useGetInsights({ from: weekPeriodsIsoStrings[3].from, to: weekPeriodsIsoStrings[3].to });

  const allInsights = [
    insightsWeek0.data?.filter((i) => i.impact) || [],
    insightsWeek1.data?.filter((i) => i.impact) || [],
    insightsWeek2.data?.filter((i) => i.impact) || [],
    insightsWeek3.data?.filter((i) => i.impact) || [],
  ];

  return (
    <Dialog open={open} onOpenChange={onOpen}>
      <Dialog.Content size='lg' className='flex h-[700px] flex-row gap-0 p-0'>
        <Dialog.Title className='sr-only' />
        <Dialog.Description className='sr-only' />
        <div
          className={cn(
            'box-content w-80 overflow-y-auto border-gray-4 border-r bg-gray-2 p-6',
            'scrollbar',
            'scrollbar-thumb-custom',
            'scrollbar-thin',
            'scrollbar-thumb-rounded-full scrollbar-track-rounded-full',
            'scrollbar-thumb-gray-8',
            'scrollbar-thumb-bg-clip-content',
            'scrollbar-track-gray-1'
          )}
        >
          <div className='mb-6'>
            <Typography variant='heading-2xl' color='secondary' className='mb-1'>
              Histórico de Insights
            </Typography>
            <Typography variant='text-sm' color='secondary' className='text-gray-9'>
              Selecione um insight para ver os detalhes
            </Typography>
          </div>
          <div className='flex flex-col gap-6'>
            {allInsights.map((insights, index) => {
              // biome-ignore lint/suspicious/noArrayIndexKey: Week index is stable identifier for week groups
              return (
                <WeekInsights
                  key={`week-${index}-${insights.length}`}
                  insights={insights}
                  selectedInsight={selectedInsight}
                  onInsightClick={handleInsightClick}
                  readInsights={readInsights}
                />
              );
            })}
          </div>
        </div>
        <div className='flex min-w-0 flex-1 flex-col'>
          <Dialog.Body className='flex h-full flex-col gap-6 overflow-y-auto p-0'>
            <Dialog.Close className='absolute top-2 right-4 z-10' />
            {!selectedInsight && <LandingContent />}
            {selectedInsight && (
              <InsightContent
                insight={selectedInsight}
                isFirstInsight={isFirstInsight}
                isLastInsight={isLastInsight}
                onPreviousInsightClick={handlePreviousInsightClick}
                onNextInsightClick={handleNextInsightClick}
              />
            )}
          </Dialog.Body>
        </div>
      </Dialog.Content>
    </Dialog>
  );
};

export default InsightsModal;
